import { useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { useTaxReport } from './useTaxReport';

/**
 * The 1099-K tax report (D-tax-reporting, Phase 7 surface 4). A PURE DERIVED READ: it computes and
 * displays the US members whose paid payouts crossed the IRS reporting threshold for a tax year.
 * The threshold crossing is an arithmetic FACT, not a determination, so the surface writes NOTHING:
 * there is no lifecycle, no "mark as filed", and no "Submit to IRS" affordance. A real 1099-K
 * generation and filing is the cross-repo migration seam.
 *
 * US-ONLY / SINGLE-JURISDICTION: fixed to `market === 'us'` in the model, NOT a region facet and
 * NOT switcher-scoped. This is the deliberate inverse of the oversight-global posture of the other
 * Phase 7 surfaces: the obligation itself is US-only. ROLE-gated to compliance and super_admin.
 */

const usd = (amount: number): string =>
  amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/**
 * The surface without the access wrapper. Exported so tests can inject `asOf` (the clock the
 * default tax year derives from: you report a CLOSED year, so the default is the most recent
 * complete year).
 */
export const TaxReportView = ({ asOf }: { asOf?: number }) => {
  const [now] = useState<number>(() => asOf ?? Date.now());
  const defaultYear = useMemo(() => new Date(now).getUTCFullYear() - 1, [now]);
  const [taxYear, setTaxYear] = useState<number>(defaultYear);
  const { view, isLoading } = useTaxReport(taxYear);

  // The year options always include the default and the selected year, merged with the years that
  // have data, most recent first, so the selector is never empty.
  const years = useMemo(() => {
    const set = new Set<number>([
      taxYear,
      defaultYear,
      ...(view?.availableYears ?? []),
    ]);
    return Array.from(set).sort((a, b) => b - a);
  }, [taxYear, defaultYear, view]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Compliance
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Tax reporting (1099-K)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        US members whose paid payouts crossed the IRS 1099-K reporting threshold
        for the tax year. The total is derived from paid payouts only: donations
        to charities are not third-party payments to the member and are
        excluded. This report is US-only because the 1099-K is a US form with no
        UK equivalent. The list is the deliverable; generating and filing a real
        1099-K is handled downstream.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="tax-year-label">Tax year</InputLabel>
          <Select
            labelId="tax-year-label"
            label="Tax year"
            value={taxYear}
            inputProps={{ 'data-testid': 'tax-year' }}
            onChange={(e) => setTaxYear(Number(e.target.value))}
          >
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading || !view ? (
        <Loading />
      ) : (
        <Box data-testid="tax-report">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Reportable members ({view.reportable.length}) for {view.taxYear}.
            Threshold {usd(view.threshold)}.
          </Typography>
          {view.reportable.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No US members crossed the {usd(view.threshold)} threshold for{' '}
              {view.taxYear}.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Member</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Paid payouts</TableCell>
                  <TableCell align="right">Reportable total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {view.reportable.map((row) => (
                  <TableRow
                    key={row.userId}
                    data-testid={`tax-row-${row.userId}`}
                  >
                    <TableCell>{row.memberName}</TableCell>
                    <TableCell>{row.memberEmail}</TableCell>
                    <TableCell align="right">{row.payoutCount}</TableCell>
                    <TableCell
                      align="right"
                      data-testid={`tax-total-${row.userId}`}
                    >
                      {usd(row.reportableTotal)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /tax. ROLE-gated to compliance and super_admin: a viewer's
 * read-everywhere '*:read' wildcard satisfies the tax:read capability, so the explicit role gate is
 * what holds the boundary (the sanctions / DSAR posture, D-tax-reporting).
 */
export const TaxReport = () => (
  <RequireAccess roles={['compliance', 'super_admin']}>
    <TaxReportView />
  </RequireAccess>
);
